<script setup lang="ts">
import {
  IconPresentation,
  IconUsers,
  IconSocial,
  IconBoxModel2,
  IconTicket,
  IconCoins,
  IconBlockquote,
  IconCertificate,
  IconCertificate2,
  IconAffiliate,
  IconWorldWww,
  IconMailBolt,
  IconCategory,
  IconCategory2,
  IconPuzzle,
  IconAdjustmentsHorizontal,
  IconChartDonut3,
  IconClock,
  IconCloudNetwork,
  IconListCheck,
  IconSearch,
  IconUsersGroup,
  IconBuilding,
  IconForms,
  IconShoppingBag,
  IconCash,
  IconReceipt,
  IconFileInvoice,
  IconMail,
  IconRss,
  IconLayoutList,
  IconDeviceDesktopAnalytics,
  IconArticle,
  IconComponents,
  IconUserCheck,
  IconCalendarEvent,
  IconCalendarTime,
  IconPresentationAnalytics,
  IconClock2,
  IconCircleDottedLetterH,
  IconLayoutGrid
} from '@tabler/icons-vue'

import apps from '@/config/apps.json'

import AppSidebar from '@/components/AppSidebar.vue'
import AppFooter from '@/components/AppFooter.vue'
import AppHeader from '@/components/AppHeader.vue'
import AppSidebarNavItem from '@/components/AppSidebarNavItem.vue'
import Statistics from '@/views/Core/Statistics/Statistics.vue'

const { initializeDatabase } = useDatabase()
initializeDatabase()

const { hasPermission, userHasRole, getUser } = useUserStore()
const eventStore = useEventStore()

const route = useRoute()
const { t } = useI18n()

// Get the current URL
const url = window.location.host
const isCompanyAdmin = userHasRole('company_administrator')
const webeditor = import.meta.env.VITE_WEBEDITOR

// Extract the domain name
const domain = url.split('.').slice(-2).join('.')
const event_id = route.params.event_id

document.cookie = `event_id=${route.params.event_id}; domain=.${domain}; path=/`
</script>
<template>
  <div>
    <AppSidebar v-if="!isCompanyAdmin" class="no-select">
      <template #navList>
        <CNavGroup>
          <template #togglerContent v-if="hasPermission('Event', 'view')">
            <IconPresentation class="navbarIcon1" stroke-width="1.2" size="27" />
            {{ t('Event') }}
          </template>
          <AppSidebarNavItem
            style="margin-left: -2px"
            :to="`/events/${event_id}/config`"
            v-if="hasPermission('Event', 'update')"
          >
            <IconAdjustmentsHorizontal class="navbarIcon1" stroke-width="1.2" size="27" />
            {{ t('Settings') }}
          </AppSidebarNavItem>
          <CNavGroup>
            <template #togglerContent v-if="hasPermission('Persona', 'view')">
              <IconUsers class="navbarIcon1" stroke-width="1.2" size="27" />
              {{ t('Participants') }}
            </template>
            <AppSidebarNavItem
              v-if="hasPermission('Persona', 'search')"
              :to="`/events/${event_id}/inscriptions/search`"
              style="margin-left: 30px"
            >
              <IconSearch class="navbarIcon1" stroke-width="1.2" size="27" />
              {{ t('Search') }}
            </AppSidebarNavItem>
            <AppSidebarNavItem :to="`/events/${event_id}/inscriptions`" style="margin-left: 30px">
              <IconUsers class="navbarIcon1" stroke-width="1.2" size="27" />
              {{ t('Personas') }}
            </AppSidebarNavItem>
            <AppSidebarNavItem
              v-if="hasPermission('PersonaGroup', 'view')"
              :to="`/events/${event_id}/groups`"
              style="margin-left: 30px"
            >
              <IconUsersGroup class="navbarIcon1" stroke-width="1.2" size="27" />
              {{ t('Groups') }}
            </AppSidebarNavItem>
            <AppSidebarNavItem :to="`/events/${event_id}/inscriptions/setup`" style="margin-left: 30px">
              <IconForms class="navbarIcon1" stroke-width="1.2" size="27" />
              {{ t('Forms') }}
            </AppSidebarNavItem>
            <CNavGroup>
              <template v-if="hasPermission('Company', 'view')" #togglerContent>
                <div style="margin-left: 30px">
                  <IconBuilding class="navbarIcon2" stroke-width="1.2" size="27" />
                  {{ t('Companies') }}
                </div>
              </template>
              <AppSidebarNavItem :to="`/events/${event_id}/companies`" style="margin-left: 60px">
                <IconBuilding class="navbarIcon2" stroke-width="1.2" size="27" />
                {{ t('Companies') }}
              </AppSidebarNavItem>
              <AppSidebarNavItem
                v-if="hasPermission('Event', 'update')"
                :to="`/events/${event_id}/companies/configuration`"
                style="margin-left: 60px"
              >
                <IconAdjustmentsHorizontal class="navbarIcon2" stroke-width="1.2" size="27" />
                {{ t('Settings') }}
              </AppSidebarNavItem>
            </CNavGroup>
          </CNavGroup>
          <CNavGroup>
            <template #togglerContent v-if="hasPermission('Activity', 'view')">
              <IconSocial class="navbarIcon1" stroke-width="1.2" size="27" />
              {{ t('Activities') }}
            </template>
            <AppSidebarNavItem
              :to="`/events/${event_id}/activities/activitytypes`"

              style="margin-left: 30px"
            >
              <IconCategory class="navbarIcon1" stroke-width="1.2" size="27" />
              {{ t('Categories') }}
            </AppSidebarNavItem>
            <AppSidebarNavItem
              :to="`/events/${event_id}/activities/activitytypecategories`"

              style="margin-left: 30px"
            >
              <IconCategory2 class="navbarIcon1" stroke-width="1.2" size="27" />
              {{ t('Subcategories') }}
            </AppSidebarNavItem>
            <AppSidebarNavItem
              :to="`/events/${event_id}/activities`"

              style="margin-left: 30px"
            >
              <IconSocial class="navbarIcon1" stroke-width="1.2" size="27" />
              {{ t('Activities') }}
            </AppSidebarNavItem>
            <AppSidebarNavItem

              :to="`/events/${event_id}/activities/inventory`"
              style="margin-left: 30px"
            >
              <IconListCheck class="navbarIcon1" stroke-width="1.2" size="27" />
              {{ t('Inventory') }}
            </AppSidebarNavItem>
          </CNavGroup>
          <AppSidebarNavItem
            :to="`/events/${event_id}/rooms`"
            :permission="[apps.interactivity.name, apps.inscriptions.name]"
            v-if="hasPermission('ConferenceRoom', 'view')"
          >
            <IconBoxModel2 class="navbarIcon1" stroke-width="1.2" size="27" />
            {{ t('Rooms') }}
          </AppSidebarNavItem>
          <AppSidebarNavItem
            :to="`/events/${event_id}/topics`"
            :permission="[apps.interactivity.name, apps.inscriptions.name, apps.webeditor.name]"
          >
            <IconBlockquote class="navbarIcon1" stroke-width="1.2" size="27" />
            {{ t('Topics') }}
          </AppSidebarNavItem>

          <!-- <CNavGroup>
            <template #togglerContent v-if="hasPermission('Schedule', 'view')">
              <IconCalendarTime class="navbarIcon1" stroke-width="1.2" size="27" />
              {{ t('Schedule') }}
            </template>
            <AppSidebarNavItem
              :to="`/events/${event_id}/schedules`"
              :permission="[apps.interactivity.name, apps.inscriptions.name, apps.webeditor.name]"
              style="margin-left: 30px"
            >
              <IconCalendarEvent class="navbarIcon1" stroke-width="1.2" size="27" />
              {{ t('Schedules') }}
            </AppSidebarNavItem>
            <AppSidebarNavItem
              :to="`/events/${event_id}/sessions`"
              :permission="[apps.interactivity.name, apps.inscriptions.name, apps.webeditor.name]"
              style="margin-left: 30px"
            >
              <IconClock2 class="navbarIcon1" stroke-width="1.2" size="27" />
              {{ t('Sessions') }}
            </AppSidebarNavItem>
            <AppSidebarNavItem
              :to="`/events/${event_id}/presentations`"
              :permission="[apps.interactivity.name, apps.inscriptions.name, apps.webeditor.name]"
              style="margin-left: 30px"
            >
              <IconPresentationAnalytics class="navbarIcon1" stroke-width="1.2" size="27" />
              {{ t('Presentations') }}
            </AppSidebarNavItem>
          </CNavGroup> -->
        </CNavGroup>

        <CNavGroup>
          <AppSidebarNavItem :to="`/events/${event_id}/accounting`">
            <IconAdjustmentsHorizontal class="navbarIcon1" stroke-width="1.2" size="27" />
            {{ t('Settings') }}
          </AppSidebarNavItem>
          <template #togglerContent>
            <IconTicket class="navbarIcon1" stroke-width="1.2" size="27" />
            {{ t('Inscriptions') }}
          </template>
          <CNavGroup>
            <template #togglerContent>
              <IconTicket class="navbarIcon1" stroke-width="1.2" size="27" />
              {{ t('Tickets') }}
            </template>

            <AppSidebarNavItem
              :to="`/events/${event_id}/tickets`"

              style="margin-left: 30px"
            >
              <IconTicket class="navbarIcon1" stroke-width="1.2" size="27" />
              {{ t('Tickets') }}
            </AppSidebarNavItem>
            <!-- <AppSidebarNavItem
              :to="`/events/${event_id}/tickets/promotionalcodes`"
              :permission="[apps.inscriptions.name]"
              style="margin-left: 30px"
            >
              <IconLayoutList class="navbarIcon1" stroke-width="1.2" size="27" />
              {{ t('Promo. codes') }}
            </AppSidebarNavItem> -->
            <AppSidebarNavItem

              :to="`/events/${event_id}/tickets/inventory`"
              style="margin-left: 30px"
            >
              <IconListCheck class="navbarIcon1" stroke-width="1.2" size="27" />
              {{ t('Inventory') }}
            </AppSidebarNavItem>
          </CNavGroup>
          <CNavGroup>
            <template #togglerContent>
              <IconCoins class="navbarIcon1" stroke-width="1.2" size="27" />
              {{ t('Accounting') }}
            </template>

            <AppSidebarNavItem :to="`/events/${event_id}/accounting/search`" style="margin-left: 30px">
              <IconSearch class="navbarIcon1" stroke-width="1.2" size="27" />
              {{ t('Search') }}
            </AppSidebarNavItem>
            <AppSidebarNavItem :to="`/events/${event_id}/accounting/purchases`" style="margin-left: 30px">
              <IconShoppingBag class="navbarIcon1" stroke-width="1.2" size="27" />
              {{ t('Orders') }}
            </AppSidebarNavItem>
            <AppSidebarNavItem :to="`/events/${event_id}/accounting/payments`" style="margin-left: 30px">
              <IconCash class="navbarIcon1" stroke-width="1.2" size="27" />
              {{ t('Payments') }}
            </AppSidebarNavItem>
            <AppSidebarNavItem :to="`/events/${event_id}/accounting/receipts`" style="margin-left: 30px">
              <IconReceipt class="navbarIcon1" stroke-width="1.2" size="27" />
              {{ t('Receipts') }}
            </AppSidebarNavItem>
            <AppSidebarNavItem :to="`/events/${event_id}/accounting/invoices`" style="margin-left: 30px">
              <IconFileInvoice class="navbarIcon1" stroke-width="1.2" size="27" />
              {{ t('Invoices') }}
            </AppSidebarNavItem>
          </CNavGroup>
          <AppSidebarNavItem :to="`/events/${event_id}/badges`">
            <IconCertificate2 class="navbarIcon1" stroke-width="1.2" size="27" />
            {{ t('Badges') }}
          </AppSidebarNavItem>
          <AppSidebarNavItem :to="`/events/${event_id}/certificates`">
            <IconCertificate class="navbarIcon1" stroke-width="1.2" size="27" />
            {{ t('Certificates') }}
          </AppSidebarNavItem>

          <!--<AppSidebarNavItem :to="`/events/${event_id}/hotels`">
            <IconCircleDottedLetterH class="navbarIcon1" stroke-width="2" size="27" />
            {{ t('Hotels') }}
          </AppSidebarNavItem>-->
        </CNavGroup>
        <AppSidebarNavItem :to="`/events/${event_id}`" class="discard margin_side">
          <IconChartDonut3 class="navbarIcon1" stroke-width="1.2" size="27" />
          {{ t('Statistics') }}
        </AppSidebarNavItem>
        <CNavGroup>
          <template #togglerContent>
            <IconCloudNetwork class="navbarIcon1" stroke-width="1.2" size="27" />
            {{ t('Communications') }}
          </template>
          <AppSidebarNavItem :to="`/events/${event_id}/email`">
            <IconMailBolt class="navbarIcon1" stroke-width="1.2" size="27" />
            {{ t('E-mail') }}
          </AppSidebarNavItem>
          <AppSidebarNavItem :to="`/events/${event_id}/push-notifications`">
            <IconAffiliate class="navbarIcon1" stroke-width="1.2" size="27" />
            {{ t('Push notifications') }}
          </AppSidebarNavItem>
        </CNavGroup>

        <!-- <CNavGroup>
          <template #togglerContent>
            <IconDeviceDesktopAnalytics class="navbarIcon1" stroke-width="1.2" size="27" />
            {{ t('Interactive and virtual') }}
          </template>
          <AppSidebarNavItem :to="`/events/${event_id}/interactive/feedwall`">
            <IconRss class="navbarIcon1" stroke-width="1.2" size="27" />
            {{ t('Feedwall') }}
          </AppSidebarNavItem>
          <AppSidebarNavItem :to="`/events/${event_id}/interactive/polls`">
            <IconMail class="navbarIcon1" stroke-width="1.2" size="27" />
            {{ t('Polls') }}
          </AppSidebarNavItem>
          <AppSidebarNavItem :to="`/events/${event_id}/interactive/pop-up`">
            <IconArticle class="navbarIcon1" stroke-width="1.2" size="27" />
            {{ t('Pop-up') }}
          </AppSidebarNavItem>
          <AppSidebarNavItem :to="`/events/${event_id}/interactive/matchup`">
            <IconComponents class="navbarIcon1" stroke-width="1.2" size="27" />
            {{ t('Matchup') }}
          </AppSidebarNavItem>
          <AppSidebarNavItem :to="`/events/${event_id}/interactive/personas-control`">
            <IconUserCheck class="navbarIcon1" stroke-width="1.2" size="27" />
            {{ t('Virtual control') }}
          </AppSidebarNavItem>
        </CNavGroup> -->

        <AppSidebarNavItem
          :to="`/events/${event_id}/realtime`"
        >
          <IconClock class="navbarIcon1" stroke-width="1.2" size="27" />
          {{ t('Realtime') }}
        </AppSidebarNavItem>

        <CNavGroup v-if="hasPermission('Web', 'create')">
          <template #togglerContent v-if="eventStore.hasApp(apps.webeditor.name)">
            <IconWorldWww class="navbarIcon1" stroke-width="1.2" size="27" />
            {{ t('Webpage') }}
          </template>
          <li class="nav-item" style="margin-left: 32px" v-if="eventStore.hasApp(apps.webeditor.name)">
            <a :href="webeditor" class="nav-link" target="__blank" style="padding-left: 17px">
              <IconPuzzle class="navbarIcon1" stroke-width="1.2" size="27" />
              {{ t('Webeditor') }}
            </a>
          </li>
        </CNavGroup>

        <CNavGroup>
          <template #togglerContent v-if="userHasRole('superadmin')">
            <IconLayoutGrid class="navbarIcon1" stroke-width="1.2" size="27" />
            {{ t('Stands') }}
          </template>

          <AppSidebarNavItem v-if="userHasRole('superadmin')" :to="`/events/${event_id}/stands/configuration`">
            <IconAdjustmentsHorizontal class="navbarIcon2" stroke-width="1.2" size="27" />
            {{ t('Settings') }}
          </AppSidebarNavItem>

          <AppSidebarNavItem v-if="userHasRole('superadmin')" :to="`/events/${event_id}/stands`">
            <IconLayoutGrid class="navbarIcon1" stroke-width="1.2" size="27" />
            {{ t('Stands') }}
          </AppSidebarNavItem>
        </CNavGroup>
      </template>
    </AppSidebar>
    <AppSidebar v-else>
      <template #navList>
        <AppSidebarNavItem :to="`/events/${event_id}/companies/edit/${getUser().company_id}`">
          {{ t('My Company') }}
        </AppSidebarNavItem>
      </template>
    </AppSidebar>
    <div class="wrapper d-flex flex-column min-vh-100 bg-light dark:bg-transparent">
      <AppHeader />
      <div class="body flex-grow-1" style="margin-bottom: 100px !important">
        <div class="container-fluid">
          <RouterView />
          <div v-if="route.name && route.name == ':event_id'">
            <Statistics />
          </div>
        </div>
      </div>
      <AppFooter />
    </div>
  </div>
</template>
